import { useEffect, useMemo, useRef, useState } from 'react';

import { PERFIL_LIDERANCA } from '../ParticipantesPesquisaClimaTab/constants';

export const createEmptyFiltro = () => ({
  formularioUsoId: null,
  tempoCargoDias: null,
  tempoEmpresaDias: null,
  cargos: null,
  areas: null,
  considerarAreasSubordinadas: false,
  perfilLideranca: PERFIL_LIDERANCA.TODOS,
  lideradosDe: null,
  lideradosDiretos: false,
  lideradosIndiretos: false,
});

// As consultas GET recebem listas de lookups por propriedades indexadas.
const obterParametrosLookup = (campo, itens) =>
  Object.fromEntries(
    (itens || []).map(({ id }, index) => [`${campo}[${index}].id`, id])
  );

export const obterParametrosFiltro = (filtro) => ({
  ...(filtro.formularioUsoId != null
    ? { formularioUsoId: filtro.formularioUsoId }
    : {}),
  tempoCargoDias: filtro.tempoCargoDias,
  tempoEmpresaDias: filtro.tempoEmpresaDias,
  ...obterParametrosLookup('cargos', filtro.cargos),
  ...obterParametrosLookup('areas', filtro.areas),
  considerarAreasSubordinadas:
    !!filtro.areas?.length && filtro.considerarAreasSubordinadas,
  perfilLideranca: filtro.perfilLideranca,
  ...obterParametrosLookup('lideradosDe', filtro.lideradosDe),
  lideradosDiretos: !!filtro.lideradosDe?.length && filtro.lideradosDiretos,
  lideradosIndiretos: !!filtro.lideradosDe?.length && filtro.lideradosIndiretos,
});

const normalizarDias = (value) => {
  if (value == null || value === '' || !Number.isFinite(Number(value)))
    return null;
  return Math.max(0, Math.min(10000, Math.trunc(Number(value))));
};

export default function useDashboardFiltros(
  filtroInicial,
  { aplicacoes = [], recorrente = false } = {}
) {
  const [filtro, setFiltro] = useState(
    () => filtroInicial || createEmptyFiltro()
  );
  const [filtroAplicado, setFiltroAplicado] = useState(
    () => filtroInicial || createEmptyFiltro()
  );
  const [open, setOpen] = useState(false);
  const ultimoUsoPadraoRef = useRef(null);
  const usoPadraoId = recorrente
    ? aplicacoes[0]?.formularioUsoId ?? null
    : null;

  useEffect(() => {
    if (!recorrente || !aplicacoes.length) return;

    const usoPadraoAnteriorId = ultimoUsoPadraoRef.current;
    const garantirUsoValido = (current) =>
      current.formularioUsoId !== usoPadraoAnteriorId &&
      aplicacoes.some((uso) => uso.formularioUsoId === current.formularioUsoId)
        ? current
        : { ...current, formularioUsoId: usoPadraoId };

    setFiltro(garantirUsoValido);
    setFiltroAplicado(garantirUsoValido);
    ultimoUsoPadraoRef.current = usoPadraoId;
  }, [aplicacoes, recorrente, usoPadraoId]);
  const parametros = useMemo(
    () => obterParametrosFiltro(filtroAplicado),
    [filtroAplicado]
  );
  const temFiltros = Object.entries(filtroAplicado).some(([field, value]) =>
    field === 'formularioUsoId'
      ? value != null && value !== usoPadraoId
      : field === 'perfilLideranca'
      ? value !== PERFIL_LIDERANCA.TODOS
      : Array.isArray(value)
      ? value.length > 0
      : value !== null && value !== false && value !== ''
  );

  const onChangeFiltro = (field, value) => {
    setFiltro((current) => ({
      ...current,
      [field]:
        field === 'tempoCargoDias' || field === 'tempoEmpresaDias'
          ? normalizarDias(value)
          : value,
      ...(field === 'areas' && !value?.length
        ? { considerarAreasSubordinadas: false }
        : {}),
      ...(field === 'lideradosDe' && !value?.length
        ? { lideradosDiretos: false, lideradosIndiretos: false }
        : {}),
      ...(field === 'lideradosDe' &&
      value?.length &&
      !current.lideradosDe?.length
        ? { lideradosDiretos: true, lideradosIndiretos: false }
        : {}),
    }));
  };

  const onAplicarFiltro = () => {
    setFiltroAplicado({ ...filtro });
    setOpen(false);
  };

  const onClearFiltro = () => {
    const vazio = {
      ...createEmptyFiltro(),
      formularioUsoId: usoPadraoId,
    };
    setFiltro(vazio);
    setFiltroAplicado(vazio);
  };

  const onRemoveFiltro = (field) => {
    const removerCampo = (current) => ({
      ...current,
      [field]:
        field === 'formularioUsoId' ? usoPadraoId : createEmptyFiltro()[field],
      ...(field === 'areas' ? { considerarAreasSubordinadas: false } : {}),
      ...(field === 'lideradosDe'
        ? { lideradosDiretos: false, lideradosIndiretos: false }
        : {}),
    });

    setFiltro(removerCampo);
    setFiltroAplicado(removerCampo);
  };

  const onOpen = () => {
    setFiltro(filtroAplicado);
    setOpen(true);
  };

  return {
    filtroAplicado,
    parametros,
    temFiltros,
    onOpen,
    onRemoveFiltro,
    drawerProps: {
      aplicacoes,
      filtro,
      open,
      recorrente,
      onChangeFiltro,
      onAplicarFiltro,
      onClearFiltro,
      onClose: () => setOpen(false),
    },
  };
}
